import React, { useMemo } from "react";
import { ChartBottomMapped, ChartDataMapped, USGAAdvancedStats } from "./types";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { VictoryContainer, VictoryPie } from "victory";
import ChartLegend from "./ChartLegend";
import ChartDetails from "./ChartDetails";

type Props = {
  data: USGAAdvancedStats;
};

const PuttingChart = ({ data }: Props) => {
  const { t } = useTranslation();
  const r = 110;

  const parAverages = useMemo(() => {
    if (!data) {
      return [];
    }

    return [
      {
        title: data.putts.toFixed(1),
        subTitle: t('Putts'),
      },
      {
        title: data.upAndDownsTotal.toFixed(1),
        subTitle: t('Putts per GIR'),
      },
    ] as ChartBottomMapped[];
  }, [data, t]);

  const pieData = useMemo(() => {
    if (!data) {
      return [];
    }

    return Object.keys(data)
      .map(key => {
        if (key === 'onePuttOrBetterPercent') {
          return {
            x: `${data[key]}%`,
            y: Math.sqrt(data[key] / 10) * r,
            color: '#43a047',
            title: t('1 Putt or Better'),
            value: `${data[key]}%`,
            index: 1,
          };
        }
        if (key === 'twoPuttPercent') {
          return {
            x: `${data[key]}%`,
            y: Math.sqrt(data[key] / 10) * r,
            color: '#CECECE',
            title: t('2 Putts'),
            value: `${data[key]}%`,
            index: 2,
          };
        }
        if (key === 'threePuttOrWorsePercent') {
          return {
            x: `${data[key]}%`,
            y: Math.sqrt(data[key] / 10) * r,
            color: '#E54500',
            title: t('3 Putts or Worse'),
            value: `${data[key]}%`,
            index: 3,
          };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => (a.index || 0) - (b.index || 0)) as ChartDataMapped[];
  }, [data, t]);

  return (
    <div className={styles.metrics_chart_container}>
      <div className={styles.chart_container}>
        <VictoryPie
          animate={false}
          height={200}
          width={200}
          padAngle={1}
          innerRadius={85}
          labelPlacement="vertical"
          labelPosition="centroid"
          containerComponent={<VictoryContainer responsive={false} />}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
            },
            labels: {
              display: 'none',
            },
          }}
          data={pieData}
          padding={0}
        />
        <div className={styles.center_max_label}>
          <div className={styles.percent}>{`${data?.twoPuttOrBetterPercent}%`}</div>
          <div className={styles.label}>{t('2 Putts or Better')}</div>
        </div>
      </div>
      <ChartLegend data={pieData} />
      <ChartDetails data={parAverages} />
    </div>
  );
};

export default PuttingChart;
